import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { confirmSubscription } from "@/lib/newsletter.functions";

const searchSchema = z.object({ token: z.string().optional() });

export const Route = createFileRoute("/newsletter/confirm")({
  validateSearch: (search) => searchSchema.parse(search),
  head: () => ({ meta: [{ title: "Newsletter Confirmed · Buddha KA" }, { name: "robots", content: "noindex" }] }),
  component: Confirm,
});

function Confirm() {
  const { token } = Route.useSearch();
  const [state, setState] = useState<"loading" | "success" | "invalid" | "error">(token ? "loading" : "invalid");

  useEffect(() => {
    if (!token) return;
    let alive = true;
    (async () => {
      try {
        const res = await confirmSubscription({ data: { token } });
        if (!alive) return;
        setState(res.ok ? "success" : "invalid");
      } catch {
        if (alive) setState("error");
      }
    })();
    return () => { alive = false; };
  }, [token]);

  return (
    <section className="mx-auto max-w-2xl px-4 py-24 text-center sm:px-6">
      <div className="mx-auto h-24 w-24 halo animate-halo rounded-full" />
      {state === "loading" && (
        <>
          <p className="mt-8 text-xs uppercase tracking-[0.3em] text-gold">Confirming</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl">Sealing your subscription…</h1>
        </>
      )}
      {state === "success" && (
        <>
          <p className="mt-8 text-xs uppercase tracking-[0.3em] text-gold">Confirmed</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl">The transmission will find you.</h1>
          <p className="mt-4 text-muted-foreground">You are now in the field. Nothing further is required of you.</p>
        </>
      )}
      {state === "invalid" && (
        <>
          <p className="mt-8 text-xs uppercase tracking-[0.3em] text-gold">Not recognized</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl">This link has expired or was already used.</h1>
          <p className="mt-4 text-muted-foreground">Subscribe again from the home page and check for a fresh confirmation.</p>
        </>
      )}
      {state === "error" && (
        <>
          <p className="mt-8 text-xs uppercase tracking-[0.3em] text-gold">Something interrupted the transmission</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl">Please try again in a moment.</h1>
        </>
      )}
      <Link to="/" className="mt-8 inline-flex rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian">Return home</Link>
    </section>
  );
}
